// Generic Config Initialization Utility
// Handles circular dependency resolution between config and validation for any module

/**
 * Creates an initialized configuration by resolving circular dependencies
 * between config and validation.
 * 
 * This utility solves the common pattern where:
 * - Config needs validation functions
 * - Validation needs config fields
 * 
 * @param {Object} config - The module configuration (documentConfig, userConfig, etc.)
 * @param {Function} validationFactory - Factory function that creates validation from fields
 * @returns {Object} - The initialized config with validation attached
 */
/**
 * Spread copies getter results as static values (e.g. tableColumns), which
 * freezes column order at first import. Re-bind getters so they stay live.
 */
const withPreservedGetters = (config, overrides = {}) => {
  const initialized = {
    ...config,
    ...overrides,
  };

  for (const key of Object.keys(config)) {
    const descriptor = Object.getOwnPropertyDescriptor(config, key);
    if (descriptor?.get) {
      Object.defineProperty(initialized, key, {
        enumerable: true,
        configurable: true,
        get: () => config[key],
      });
    }
  }

  return initialized;
};

export const createInitializedConfig = (config, validationFactory) => {
  if (typeof validationFactory !== 'function') {
    return withPreservedGetters(config, {
      validateField: config.validateField ?? null,
      validateData: config.validateData ?? null,
      validationSchema: config.validationSchema ?? null,
    });
  }

  try {
    // Create validation functions using the config fields
    const validation = validationFactory(config.fields);
    
    // Return config with validation methods attached
    return withPreservedGetters(config, {
      validateField: validation.validateField,
      validateData:
        validation.validateData ||
        validation.validateDocumentData ||
        validation.validateProcedureData,
      validationSchema: validation.schema,
    });
  } catch (error) {
    console.error('Error initializing config with validation:', error);
    // Return config without validation if initialization fails
    return withPreservedGetters(config, {
      validateField: null,
      validateData: null,
      validationSchema: null,
    });
  }
};

/**
 * Creates an initialized service instance with the initialized config
 * 
 * @param {Class} ServiceClass - The service class to instantiate
 * @param {Object} initializedConfig - The config with validation attached
 * @returns {Object} - Service instance with the initialized config
 */
export const createInitializedService = (ServiceClass, initializedConfig) => {
  try {
    return new ServiceClass(initializedConfig);
  } catch (error) {
    console.error('Error creating initialized service:', error);
    throw error;
  }
};

/**
 * Complete module initialization factory
 * Handles config initialization (services no longer needed)
 * 
 * @param {Object} config - The module configuration
 * @param {Function} validationFactory - Factory function for validation
 * @param {Class} ServiceClass - DEPRECATED: The service class (no longer used)
 * @returns {Object} - Object containing initialized config
 */
export const initializeModule = (config, validationFactory, ServiceClass = null) => {
  const initializedConfig = createInitializedConfig(config, validationFactory);
  
  // Legacy support: if ServiceClass is provided, create service (but we no longer use it)
  if (ServiceClass) {
    console.warn('ServiceClass parameter is deprecated. Services are replaced by SWR hooks.');
    const service = createInitializedService(ServiceClass, initializedConfig);
    return {
      config: initializedConfig,
      service
    };
  }
  
  // New approach: return only config, data fetching handled by SWR hooks
  return {
    config: initializedConfig
  };
};

// Export individual functions for flexibility
export default {
  createInitializedConfig,
  createInitializedService,
  initializeModule
};
